import React from 'react'
import { Box, Paper, Grid, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { motion } from 'framer-motion'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { EducationTooltip, getEducationMessages } from './Education'

const RoyalAnimatedCard = styled(motion(Paper))`
  background: #0a0a0a;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(120, 60, 180, 0.3);
  border: 1px solid rgba(165, 121, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #8a4fff, #d946ef, #8a4fff);
  }
`

const MetricCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(3),
  background: `rgba(${color}, 0.1)`,
  borderRadius: '12px',
  textAlign: 'center',
  border: `1px solid rgba(${color}, 0.3)`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px rgba(${color}, 0.2)`,
  },
}))

const RoyalTitle = styled(Typography)`
  font-family: 'Cinzel', serif;
  font-weight: 600;
  letter-spacing: 1px;
  background: linear-gradient(90deg, #ffffff, #d1b3ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 2px;
    background: linear-gradient(90deg, #8a4fff, transparent);
    margin: 12px 0 24px;
  }
`

const COLORS = ['#8a4fff', '#d946ef', '#ff6b6b', '#ff9e00', '#46efc8', '#7b2cbf']

const Dashboard = ({ result, portfolio, availableAssets }) => {
  const educationMessages = getEducationMessages()

  const chartData = result?.sharesBreakdown?.length > 0
    ? result.sharesBreakdown.map(asset => ({
        name: asset.symbol,
        value: asset.percentage,
      }))
    : [];

  console.log('Chart Data:', chartData)

  return (
    <RoyalAnimatedCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <RoyalTitle variant="h4">Portfolio Dashboard</RoyalTitle>
      {!result && (
        <Typography color="#a0a0a0" fontStyle="italic" sx={{ mb: 4 }}>
          Calculate your portfolio to view results
        </Typography>
      )}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <MetricCard color="138, 79, 255">
            <Typography variant="caption" color="#b0b0b0" sx={{ display: 'block', mb: 1 }}>
              <EducationTooltip title={educationMessages.portfolioYield}>Portfolio Yield</EducationTooltip>
            </Typography>
            <Typography variant="h4" sx={{ 
              background: 'linear-gradient(90deg, #8a4fff, #d946ef)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}>
              {result && result.portfolioYield ? `${(result.portfolioYield * 100).toFixed(2)}%` : '-'}
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard color="217, 70, 239">
            <Typography variant="caption" color="#b0b0b0" sx={{ display: 'block', mb: 1 }}>
              Total Investment
            </Typography>
            <Typography variant="h4" sx={{ 
              background: 'linear-gradient(90deg, #d946ef, #ff6b6b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}>
              {result && result.totalValue ? `$${result.totalValue.toFixed(2)}` : '-'}
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard color="255, 107, 107">
            <Typography variant="caption" color="#b0b0b0" sx={{ display: 'block', mb: 1 }}>
              Annual Dividend
            </Typography>
            <Typography variant="h4" sx={{ 
              background: 'linear-gradient(90deg, #ff6b6b, #ff9e00)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}>
              {result && result.annualDividend ? `$${result.annualDividend.toFixed(2)}` : '-'}
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard color="255, 158, 0">
            <Typography variant="caption" color="#b0b0b0" sx={{ display: 'block', mb: 1 }}>
              <EducationTooltip title={educationMessages.endOfYearValue}>End of Year Value</EducationTooltip>
            </Typography>
            <Typography variant="h4" sx={{ 
              background: 'linear-gradient(90deg, #ff9e00, #46efc8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}>
              {result && result.endOfYearValue ? `$${result.endOfYearValue.toFixed(2)}` : '-'}
            </Typography>
          </MetricCard>
        </Grid>
      </Grid>
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ color: '#e0e0e0', mb: 2 }}>
          Portfolio Breakdown
        </Typography>
        {chartData.length > 0 ? (
          <Box sx={{ height: 300, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value.toFixed(1)}%`}
                  contentStyle={{
                    background: '#0a0a0a',
                    border: '1px solid rgba(165, 121, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#ffffff'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography color="#a0a0a0" fontStyle="italic">
            Add assets to your portfolio and calculate to view the breakdown
          </Typography>
        )}
      </Box>
    </RoyalAnimatedCard>
  )
}

export default Dashboard